import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useMemo } from 'react';
import { StagesTableV2 } from '@/features/pipelines/components/StagesTableV2';
import { queryPipelineStages } from '@/features/pipelines/data';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { useDateWindow, type DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { TagOption } from '@/shared/data/tags';
import type { ApiSort } from '@/ui/composites/grid/gridUtils';
import type { BuildStatus, PipelineStageListItemDto, QueryResult } from '@flowqa/shared';

export type StageSortKey =
  | 'pipelineName'
  | 'stageName'
  | 'startedAt'
  | 'status'
  | 'executionTotal'
  | 'executionFrequencyPerDay'
  | 'executionAverageDurationMs'
  | 'executionPassRate'
  | 'executionFlakeScore';

export type StagesTablePanelProps = {
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  tagOptions: TagOption[];
  dateQuickFilter: DateQuickFilter;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  latestQueryStartDay: string | null;
  latestQueryEndDay: string | null;
  projectOptions?: Array<{ value: string; label: string }>;
  stageSearchValue: string;
  stageSortKey: StageSortKey;
  stageSortDirection: 'asc' | 'desc';
  onStageSortChange: (nextSortKey: StageSortKey, nextSortDirection: 'asc' | 'desc') => void;
  stageFilterStatuses: BuildStatus[];
  stageFilterPipelineIds: string[];
  stageFilterStageNames: string[];
  stageFilterTags: string[];
};

export const StagesTablePanel = ({
  selectedProjectId,
  selectedOrganisationId,
  tagOptions,
  dateQuickFilter,
  fromDate,
  toDate,
  latestQueryStartDay,
  latestQueryEndDay,
  projectOptions = [],
  stageSearchValue,
  stageSortKey,
  stageSortDirection,
  onStageSortChange,
  stageFilterStatuses,
  stageFilterPipelineIds,
  stageFilterStageNames,
  stageFilterTags,
}: StagesTablePanelProps): ReactElement => {
  const dateWindow = useDateWindow({
    dateQuickFilter,
    fromDate,
    toDate,
    latestQueryStartDay,
    latestQueryEndDay,
  });
  const stageStatusFilterKey = useMemo(
    () => [...stageFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [stageFilterStatuses],
  );
  const stageTagFilterKey = useMemo(
    () => [...stageFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [stageFilterTags],
  );
  const stagePipelineFilterKey = useMemo(
    () => [...stageFilterPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
    [stageFilterPipelineIds],
  );
  const stageNameFilterKey = useMemo(
    () => [...stageFilterStageNames].sort((left, right) => left.localeCompare(right)).join(','),
    [stageFilterStageNames],
  );

  return (
    <Stack gap="xl">
      <StagesTableV2
        queryKey={queryKeys.dataGrid(
          'stages/table',
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          dateQuickFilter,
          stageSearchValue,
          stageSortKey,
          stageSortDirection,
          stageStatusFilterKey,
          stagePipelineFilterKey,
          stageNameFilterKey,
          stageTagFilterKey,
          dateWindow.startDay,
          dateWindow.endDay,
        )}
        queryFn={async (input): Promise<QueryResult<PipelineStageListItemDto>> =>
          await queryPipelineStages(selectedProjectId, selectedOrganisationId ?? undefined, {
            ...input,
            sorts:
              input.sorts.length > 0
                ? input.sorts
                : ([{ field: stageSortKey, direction: stageSortDirection }] satisfies ApiSort[]),
            filters: [
              ...input.filters,
              ...mergeFilters(
                buildContainsFilter('stageName', stageSearchValue),
                buildInFilter('status', stageFilterStatuses),
                buildInFilter('pipelineId', stageFilterPipelineIds),
                buildInFilter('stageName', stageFilterStageNames),
                buildInFilter('tags', stageFilterTags),
                buildDateRangeFilters(
                  'startedAt',
                  dateWindow.startDay
                    ? dayjs(dateWindow.startDay).startOf('day').toISOString()
                    : null,
                  dateWindow.endDay ? dayjs(dateWindow.endDay).endOf('day').toISOString() : null,
                ),
              ),
            ],
          })
        }
        pageSizeOptions={[10, 25, 50]}
        projectOptions={projectOptions}
        noRowsText={formatRangeWindowNoDataText(
          getEmptyStateMessage('entity.noneFound', { entity: 'stage' }),
          dateQuickFilter,
          dateWindow.hasCustomBounds,
        )}
        search={stageSearchValue}
        showProjectColumn={selectedProjectId === null}
        sort={{ field: stageSortKey, direction: stageSortDirection }}
        stageRunsEndAt={
          dateWindow.endDay ? dayjs(dateWindow.endDay).endOf('day').toISOString() : null
        }
        stageRunsStartAt={
          dateWindow.startDay ? dayjs(dateWindow.startDay).startOf('day').toISOString() : null
        }
        tagOptionsOverride={tagOptions}
        onSortChange={(nextSort) => {
          onStageSortChange(nextSort.field as StageSortKey, nextSort.direction);
        }}
      />
    </Stack>
  );
};
